<template>
  <div class="execution-list-container">
    <div class="list-header">
      <h1>Execuções</h1>
      <div class="filter-controls">
        <select v-model="statusFilter" class="filter-select">
          <option value="all">Todos os status</option>
          <option value="RUNNING">Em execução</option>
          <option value="COMPLETED">Concluído</option>
          <option value="FAILED">Falha</option>
          <option value="PENDING">Pendente</option>
          <option value="CANCELLED">Cancelado</option>
        </select>
        <button @click="fetchExecutions" class="btn btn-outline refresh-btn">
          <i class="fas fa-sync-alt"></i> Atualizar
        </button>
      </div>
    </div>

    <div v-if="loading" class="loading-container">
      <div class="loading-spinner"></div>
      <p>Carregando execuções...</p>
    </div>

    <div v-else-if="filteredExecutions.length === 0" class="empty-state">
      <div class="empty-icon">
        <i class="fas fa-play-circle"></i>
      </div>
      <h2>Nenhuma execução encontrada</h2>
      <p>Execute um pipeline para visualizar os resultados aqui.</p>
      <router-link to="/pipelines" class="btn btn-primary">
        Ver Pipelines
      </router-link>
    </div>

    <div v-else class="execution-table-container">
      <table class="execution-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Pipeline</th>
            <th>Status</th>
            <th>Início</th>
            <th>Fim</th>
            <th>Duração</th>
            <th>Ações</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="execution in filteredExecutions" :key="execution.id" class="execution-row">
            <td>{{ execution.id }}</td>
            <td>{{ execution.pipeline_name }}</td>
            <td>
              <span class="status-badge" :class="'status-' + execution.status.toLowerCase()">
                {{ execution.status }}
              </span>
            </td>
            <td>{{ formatDate(execution.start_time) }}</td>
            <td>{{ formatDate(execution.end_time) }}</td>
            <td>{{ calculateDuration(execution) }}</td>
            <td class="actions-cell">
              <router-link :to="`/executions/${execution.id}`" class="btn btn-sm btn-outline">
                <i class="fas fa-eye"></i>
              </router-link>
              <button 
                v-if="execution.status === 'RUNNING' || execution.status === 'PENDING'"
                @click="cancelExecution(execution.id)" 
                class="btn btn-sm btn-danger"
              >
                <i class="fas fa-stop"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ExecutionList',
  data() {
    return {
      loading: false,
      statusFilter: 'all',
      refreshInterval: null
    }
  },
  computed: {
    executions() {
      return this.$store.getters.getExecutions || []
    },
    filteredExecutions() {
      if (this.statusFilter === 'all') {
        return this.executions
      }
      return this.executions.filter(execution => execution.status === this.statusFilter)
    }
  },
  methods: {
    async fetchExecutions() {
      this.loading = true
      try {
        await this.$store.dispatch('fetchExecutions')
      } catch (error) {
        console.error('Erro ao buscar execuções:', error)
        this.$toast.error('Erro ao carregar execuções')
      } finally {
        this.loading = false
      }
    },
    
    async cancelExecution(id) {
      try {
        const success = await this.$store.dispatch('cancelExecution', id)
        if (success) {
          this.$toast.success('Execução cancelada com sucesso!')
          this.fetchExecutions()
        } else {
          this.$toast.error(this.$store.getters.getError || 'Erro ao cancelar execução')
        }
      } catch (error) {
        console.error('Erro ao cancelar execução:', error)
        this.$toast.error('Erro ao cancelar execução')
      }
    },
    
    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleString('pt-BR')
    },
    
    calculateDuration(execution) {
      if (!execution.start_time) return '-'
      
      const start = new Date(execution.start_time)
      const end = execution.end_time ? new Date(execution.end_time) : new Date()
      
      const durationMs = end - start
      
      if (durationMs < 1000) {
        return `${durationMs}ms`
      } else if (durationMs < 60000) {
        return `${Math.floor(durationMs / 1000)}s`
      } else if (durationMs < 3600000) {
        const minutes = Math.floor(durationMs / 60000)
        const seconds = Math.floor((durationMs % 60000) / 1000)
        return `${minutes}m ${seconds}s`
      } else {
        const hours = Math.floor(durationMs / 3600000)
        const minutes = Math.floor((durationMs % 3600000) / 60000)
        return `${hours}h ${minutes}m`
      }
    },
    
    setupAutoRefresh() {
      // Atualizar a lista a cada 30 segundos para execuções em andamento
      this.refreshInterval = setInterval(() => {
        const hasRunningExecutions = this.executions.some(
          execution => execution.status === 'RUNNING' || execution.status === 'PENDING'
        )
        
        if (hasRunningExecutions) {
          this.fetchExecutions()
        }
      }, 30000)
    }
  },
  created() {
    this.fetchExecutions()
    this.setupAutoRefresh()
  },
  beforeUnmount() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval)
    }
  }
}
</script>

<style scoped>
.execution-list-container {
  padding: 2rem;
}

.list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.list-header h1 {
  margin: 0;
  color: #1e293b;
}

.filter-controls {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.filter-select {
  padding: 0.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  background-color: white;
  color: #1e293b;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: all 0.2s;
}

.btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

.btn-primary {
  background-color: #3b82f6;
  color: white;
}

.btn-primary:hover {
  background-color: #2563eb;
}

.btn-outline {
  background-color: transparent;
  border: 1px solid #cbd5e1;
  color: #64748b;
}

.btn-outline:hover {
  border-color: #94a3b8;
  color: #475569;
}

.btn-danger {
  background-color: #ef4444;
  color: white;
}

.btn-danger:hover {
  background-color: #dc2626;
}

.refresh-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #e2e8f0;
  border-top: 4px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem;
  text-align: center;
}

.empty-icon {
  font-size: 3rem;
  color: #cbd5e1;
  margin-bottom: 1rem;
}

.empty-state h2 {
  margin: 0 0 0.5rem 0;
  color: #1e293b;
}

.empty-state p {
  margin: 0 0 1.5rem 0;
  color: #64748b;
  max-width: 400px;
}

.execution-table-container {
  overflow-x: auto;
}

.execution-table {
  width: 100%;
  border-collapse: collapse;
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.execution-table th,
.execution-table td {
  padding: 1rem;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
}

.execution-table th {
  background-color: #f8fafc;
  font-weight: 600;
  color: #475569;
}

.execution-table tr:last-child td {
  border-bottom: none;
}

.execution-row:hover {
  background-color: #f8fafc;
}

.status-badge {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
}

.status-completed {
  background-color: #dcfce7;
  color: #166534;
}

.status-running {
  background-color: #dbeafe;
  color: #1e40af;
}

.status-pending {
  background-color: #fef3c7;
  color: #92400e;
}

.status-failed {
  background-color: #fee2e2;
  color: #b91c1c;
}

.status-cancelled {
  background-color: #f3f4f6;
  color: #4b5563;
}

.actions-cell {
  display: flex;
  gap: 0.5rem;
}
</style>
